const DB_NAME = 'bodynodelink';
const DB_VERSION = 1;
const STORE = 'recordings';

export interface RecordingBlob {
  id: string;
  createdAt: number;
  videoBlob: Blob;
  videoMime: string;
  /** Compact binary pose export (gzipped). See src/modules/PoseBinaryFormat.ts. */
  poseBin: Uint8Array;
  /** Optional v3.2 CanonicalPersistenceFormat payload, written
   *  alongside `poseBin` when the host enables the canonical save
   *  pipeline. Existing readers can ignore it; canonical-aware
   *  readers decode via decodeCanonicalRecording(). */
  canonicalBin?: Uint8Array | null;
  engineId?: string;
  engineLabel?: string;
  resolution?: { width: number; height: number };
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function withIdbLock<T>(fn: () => Promise<T>): Promise<T> {
  if (typeof navigator !== 'undefined' && 'locks' in navigator) {
    return navigator.locks.request('bnl-idb-recordings', fn);
  }
  return fn();
}

export async function saveRecording(rec: RecordingBlob): Promise<void> {
  await withIdbLock(async () => {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(rec);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  });
}

export async function loadRecording(id: string): Promise<RecordingBlob | null> {
  const db = await openDb();
  const rec = await new Promise<RecordingBlob | null>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(id);
    req.onsuccess = () => resolve((req.result as RecordingBlob) ?? null);
    req.onerror = () => reject(req.error);
  });
  db.close();
  return rec;
}

export interface RecordingIndexEntry {
  id: string;
  createdAt: number;
  videoMime: string;
  videoSize: number;
  engineId?: string;
  engineLabel?: string;
  resolution?: { width: number; height: number };
  poseBytes: number;
}

export async function listRecordings(): Promise<RecordingIndexEntry[]> {
  const db = await openDb();
  const all = await new Promise<RecordingBlob[]>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => resolve((req.result as RecordingBlob[]) ?? []);
    req.onerror = () => reject(req.error);
  });
  db.close();
  return all
    .sort((a, b) => b.createdAt - a.createdAt)
    .map((r) => ({
      id: r.id,
      createdAt: r.createdAt,
      videoMime: r.videoMime,
      videoSize: r.videoBlob?.size ?? 0,
      engineId: r.engineId,
      engineLabel: r.engineLabel,
      resolution: r.resolution,
      poseBytes: r.poseBin?.byteLength ?? 0,
    }));
}

export async function deleteRecording(id: string): Promise<void> {
  await withIdbLock(async () => {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  });
}

export async function loadLatestRecording(): Promise<RecordingBlob | null> {
  const db = await openDb();
  const all = await new Promise<RecordingBlob[]>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => resolve((req.result as RecordingBlob[]) ?? []);
    req.onerror = () => reject(req.error);
  });
  db.close();
  if (all.length === 0) return null;
  all.sort((a, b) => b.createdAt - a.createdAt);
  return all[0];
}

export async function pruneOldRecordings(keepLatest = 3): Promise<void> {
  await withIdbLock(async () => {
    const db = await openDb();
    const all = await new Promise<RecordingBlob[]>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).getAll();
      req.onsuccess = () => resolve((req.result as RecordingBlob[]) ?? []);
      req.onerror = () => reject(req.error);
    });
    all.sort((a, b) => b.createdAt - a.createdAt);
    const toDelete = all.slice(keepLatest);
    if (toDelete.length === 0) {
      db.close();
      return;
    }
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const s = tx.objectStore(STORE);
      for (const r of toDelete) s.delete(r.id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  });
}

export function newRecordingId(): string {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}
